// src/components/NearbyPlacesMap.jsx
import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

const API_URL = process.env.REACT_APP_API_URL;

// Default Leaflet ikonlarının bundler'da kaybolmasını engellemek için manuel ikon tanımı
const defaultIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});

const primaryIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [30, 49], // hedef mekanda biraz daha büyük ikon
  iconAnchor: [15, 49],
  popupAnchor: [1, -40],
  shadowSize: [49, 49],
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    const bounds = L.latLngBounds(points.map(p => [p.lat, p.lng]));
    map.fitBounds(bounds.pad(0.2));
  }, [points, map]);
  return null;
}

export default function NearbyPlacesMap({ placeName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!placeName) return;
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${API_URL}/api/nearby/${encodeURIComponent(placeName)}?k=3`);
        if (!res.ok) throw new Error(`API ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [placeName, ]);

  const points = useMemo(() => {
    if (!data) return [];
    const arr = [];
    if (data.target) arr.push({ ...data.target, role: "target" });
    if (Array.isArray(data.nearby)) arr.push(...data.nearby.map(x => ({ ...x, role: "nearby" })));
    return arr;
  }, [data]);

  if (!placeName) return <div className="p-4 text-sm">Bir mekan adı (placeName) verin.</div>;
  if (loading) return <div className="p-4 text-sm">Yükleniyor…</div>;
  if (error) return <div className="p-4 text-sm text-red-600">Hata: {error}</div>;
  if (!data || !data.target) return <div className="p-4 text-sm">Veri bulunamadı.</div>;

  const center = [data.target.lat, data.target.lng];
  let no=0

  return (
    <div className="w-full h-[480px] rounded-2xl overflow-hidden shadow">
      <MapContainer center={center} zoom={15} style={{ width: "100%", height: "100%" }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> katkıda bulunanlar'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {/* Otomatik kadraj */}
        <FitBounds points={points} />

        {/* Hedef mekân */}
        <Marker position={center} icon={primaryIcon}>
          <Popup>
            <strong>{data.target.name}</strong>
            <div className="text-xs mt-1">{data.target.lat.toFixed(6)}, {data.target.lng.toFixed(6)}</div>
          </Popup>
        </Marker>
        {/* Yakın mekanlar */}
        {data.nearby?.map((p, idx) => (
          <Marker key={idx} position={[p.lat, p.lng]} icon={defaultIcon}>
            <Popup>
              <strong>{no=no+1}- {p.name}</strong>
              <div className="text-xs mt-1">
                {p.lat.toFixed(6)}, {p.lng.toFixed(6)}
                {typeof p.distance_m === "number" && <div>Mesafe: ~{Math.round(p.distance_m)} m</div>}
                <a 
                href={`https://www.google.com/maps/search/${encodeURIComponent(p.name)}`} 
                target="_blank" 
                rel="noopener noreferrer"
                >
                Google Haritalar
                </a>
              </div>
            </Popup>
          </Marker>
        ))}

        {/* Hedef ile yakınları birleştiren çizgiler (görsel ipucu) */}
        {data.nearby?.map((p, idx) => (
          <Polyline key={`line-${idx}`} positions={[
            [data.target.lat, data.target.lng],
            [p.lat, p.lng],
          ]} />
        ))}

        {/* Hedef çevresinde 200m daire (isteğe bağlı) */}
        <CircleMarker center={center} radius={6} />
      </MapContainer>
    </div>
  );
}